import {
  useDeleteSurveyMutation,
  useGetSurveysByOrgIdLazyQuery,
  useGetSurveysQuery,
} from "graphql/__generated__/hooks";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Survey } from "types/survey";

/**
 * hook to share Survey list functionalities
 *
 * - surveys: Survey[] List of surveys
 * - loading: boolean
 * - error: Error
 * - handleActionClick: function Handle when user click an action element.
 * - deleteSurveyOpen: boolean Display the Delete alert.
 * - setDeleteSurveyOpen: function to change state of delete alert.
 * - surveyIdToDelete: id of the Survey element to delete
 * - handleDelete: Function to delete an element.
 */
export const useSurvey = () => {
  const navigate = useNavigate();
  const [deleteSurveyOpen, setDeleteSurveyOpen] = useState(false);
  const [surveyIdToDelete, setSurveyIdToDelete] = useState("");
  const { organizationId } = useParams();
  const isOrgView = useMemo(() => {
    return !!organizationId;
  }, [organizationId]);

  const [deleteSurvey] = useDeleteSurveyMutation();
  const { notify } = useNotifications();

  const { data, error, loading: surveysLoading, refetch } = useGetSurveysQuery();
  const [
    getSurveysByOrgId,
    { data: orgData, loading: orgLoading },
  ] = useGetSurveysByOrgIdLazyQuery();

  const loading = useMemo(() => {
    return surveysLoading || orgLoading;
  }, [surveysLoading, orgLoading]);

  const fetchOrgSurveys = useCallback(() => {
    if (organizationId) {
      getSurveysByOrgId({
        variables: { orgId: organizationId },
      });
    }
  }, [organizationId, getSurveysByOrgId]);

  useEffect(() => {
    if (organizationId) {
      fetchOrgSurveys();
    }
  }, [organizationId, fetchOrgSurveys]);

  const surveys = useMemo(() => {
    if (organizationId) {
      return (orgData?.survey as Survey[]) || [];
    }
    // In admin view, only show surveys without org_id
    const allSurveys = (data?.survey as Survey[]) || [];
    return allSurveys.filter((survey) => !survey.org_id);
  }, [organizationId, orgData, data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/survey/${id}`,
        );
        break;
      case TABLE_ACTIONS.EDIT:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/survey/${id}/edit`,
        );
        break;
      case TABLE_ACTIONS.DELETE:
        setSurveyIdToDelete(id);
        setDeleteSurveyOpen(true);
        break;
      case TABLE_ACTIONS.VIEW_ANSWERS:
        if (organizationId) {
          navigate(`/organization/${organizationId}/survey/${id}/answers`);
        } else {
          navigate(`/survey/${id}/answers`);
        }
        break;
      case TABLE_ACTIONS.ANSWER_SURVEY:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/survey/${id}/answer`,
        );
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!surveyIdToDelete || surveyIdToDelete === "") {
      return;
    }
    deleteSurvey({
      variables: { id: surveyIdToDelete },
    })
      .then(async () => {
        if (organizationId) {
          await fetchOrgSurveys();
        } else {
          await refetch();
        }
        setSurveyIdToDelete("");
        notify({
          message: Messages.successRequirementDeleted,
          severity: NotificationSeverity.success,
        });
      })
      .catch((error) => {
        if (error.cause?.extensions?.code === "constraint-violation") {
          notify({
            message:
              "This survey is currently in use and cannot be deleted.",
            severity: NotificationSeverity.error,
          });
        } else {
          notify({
            message: "Error deleting survey",
            severity: NotificationSeverity.error,
          });
        }
      });
  };

  return {
    surveys,
    loading,
    isOrgView,
    organizationId,
    error,
    deleteSurveyOpen,
    surveyIdToDelete,
    handleActionClick,
    setDeleteSurveyOpen,
    handleDelete,
  };
};
